/**
 * CostSanityWarning — inline banner shown when the entered cost yields a
 * per-unit-of-measure rate suspiciously below this product's own
 * historical average.
 *
 * Why it exists:
 *   Root cause of the Aug 2026 Chameleon-film bug: staff typed the
 *   per-meter rate (€30) into a per-roll cost field, so a €90 receipt
 *   was stored as €30. Formula then produced €10/m job costs instead
 *   of €30/m. This component catches that class of typo AT INPUT TIME.
 *
 * Rules (must match the backend baseline endpoint's response):
 *   * Fires ONLY when `baseline.insufficient === false` — a brand-new
 *     product with <3 prior costed deliveries has no meaningful average
 *     and any warning on it would be a false positive. Render null in
 *     that case.
 *   * Fires when `currentPerUom / baseline.avg_cost_per_uom < 0.50`.
 *     50% chosen because:
 *       - 20% supplier discount + 25% seasonal variation ≈ safely inside
 *         the "not suspicious" band.
 *       - A per-meter-vs-per-roll typo on typical film (10× ratio for a
 *         30m roll) blows through 50% easily.
 *     Tighter thresholds fired on genuine bulk-buy discounts; looser
 *     ones missed real typos on partial rolls.
 *   * User can dismiss the warning inline ("Yes, that's correct") so a
 *     legitimate sale price / new supplier doesn't need a workaround.
 *     Dismissal is per-modal-open (state resets on next open) — we DO
 *     NOT persist "always allow" per product; treat every input as
 *     fresh so a second typo tomorrow still gets flagged.
 *
 * Contract:
 *   <CostSanityWarning
 *     baseline={{ avg_cost_per_uom, min_cost_per_uom, base_unit,
 *                 product_name, insufficient }}
 *     currentPerUom={number}
 *     onOverride={() => setAccepted(true)}
 *     overridden={boolean}
 *   />
 */
import React from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";

const SUSPICIOUS_RATIO_THRESHOLD = 0.5;

// UoM label matches the same lookup used by the delivery + edit modals.
const uomLabel = (baseUnit) => {
  if (baseUnit === "cm") return "meter";
  if (baseUnit === "ml") return "ml";
  return "unit";
};

const CostSanityWarning = ({ baseline, currentPerUom, onOverride, overridden }) => {
  // Insufficient history → do not render. Explicit guard so callers
  // can pass the raw endpoint response without a pre-filter.
  if (!baseline || baseline.insufficient) return null;
  if (!currentPerUom || currentPerUom <= 0) return null;
  const avg = baseline.avg_cost_per_uom;
  if (!avg || avg <= 0) return null;

  const ratio = currentPerUom / avg;
  if (ratio >= SUSPICIOUS_RATIO_THRESHOLD) return null;

  const unit = uomLabel(baseline.base_unit);
  const pct = Math.round(ratio * 100);

  if (overridden) {
    // Once acknowledged, downgrade the banner to a quiet confirmation
    // so the admin can still see the numbers but the "warning" tone
    // goes away. Keeps the modal calm after confirm.
    return (
      <div
        className="flex items-start gap-2 text-emerald-400 text-xs bg-emerald-500/5 border border-emerald-500/20 rounded p-2"
        data-testid="cost-sanity-warning-overridden"
      >
        <span className="text-emerald-300">✓</span>
        <span>
          Confirmed: €{currentPerUom.toFixed(2)}/{unit} accepted despite being
          {" "}{pct}% of the usual €{avg.toFixed(2)}/{unit}.
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 text-amber-300 text-xs bg-amber-500/10 border border-amber-500/30 rounded p-3"
      data-testid="cost-sanity-warning"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
        <div>
          <p className="font-medium text-amber-200">
            This works out to €{currentPerUom.toFixed(2)}/{unit}, well under{" "}
            {baseline.product_name || "this product"}&apos;s usual €{avg.toFixed(2)}/{unit}
            — is the cost per-{unit} by mistake?
          </p>
          <p className="text-amber-400/80 mt-1">
            Based on {baseline.sample_size} prior costed deliveries
            (range €{(baseline.min_cost_per_uom || 0).toFixed(2)}–€{(baseline.max_cost_per_uom || 0).toFixed(2)}/{unit}).
          </p>
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onOverride}
        className="self-start border-amber-500/40 text-amber-200 hover:bg-amber-500/10 h-7 text-xs"
        data-testid="cost-sanity-override-btn"
      >
        Yes, that&apos;s correct
      </Button>
    </div>
  );
};

export default CostSanityWarning;
